import styled, { css } from 'styled-components';
import { lighten, rgba } from 'polished';
import { palette } from '@awarego-ui/components';
import { toValidColor } from '@awarego-ui/components/utils/helpers';
import defaultValidatorFn from '@emotion/is-prop-valid';

const themeColor = (props) => toValidColor(props?.color || props?.theme?.primary);

export const ThemedStep = styled.div.attrs((props) => ({
    className: [
        'ag-player-step',
        props.active && 'ag-player-step-active',
        props.enabled && 'ag-player-step-enabled',
        props.viewed && 'ag-player-step-viewed',
        props.answered && 'ag-player-step-answered',
        props.incorrect && 'ag-player-step-incorrect',
        props.certificate && 'ag-player-step-certificate',
        props.assessment && 'ag-player-step-quiz',
    ]
        .filter(Boolean)
        .join(' '),
}))`
    position: relative;
    white-space: nowrap;
    line-height: 36px;
    padding-left: 30px;
    padding-right: 10px;
    font-size: 12px;
    text-transform: uppercase;
    color: #767676;
    font-weight: 700;
    border-left-color: white;
    border-left-style: solid;
    border-left-width: 4px;
    border-right-color: white;
    justify-content: space-between;
    flex-direction: row;
    display: flex;
    letter-spacing: 0.04em;
    padding-right: 20px;
    a {
        width: 100%;
    }
    &:after {
        position: absolute;
        content: '';
        width: 20px;
        height: 36px;
        right: 0px;
        top: 0px;
        background-position: center center;
        background-repeat: no-repeat;
        background-size: 14px auto;
        transition: ease 0.2s all;
    }

    ${(props) =>
        props.enabled &&
        css`
            opacity: 1;
            cursor: pointer;
            color: ${palette.graphite.heavyMetal};
            &:hover {
                color: ${(props) =>
                    lighten(0.1, themeColor(props))};
                border-left-color: ${lighten(0.4, palette.graphite.heavyMetal)};
                border-right-color: ${lighten(
                    0.4,
                    palette.graphite.heavyMetal
                )};
            }
            &:after {
                position: absolute;
                content: '';
                width: 20px;
                height: 36px;
                right: 0px;
                top: 0px;
                background-size: 14px auto;
            }
        `};
    ${(props) =>
        props.active &&
        css`
            border-left-color: ${(props) => themeColor(props)};
            border-right-color: ${(props) => themeColor(props)};
            border-left-style: solid ${(props) => themeColor(props)};
            border-left-width: 4px;
            a {
                color: ${(props) => themeColor(props)};
                font-weight: 800;
            }
            span {
                font-weight: 600;
            }
        `};
    ${(props) =>
        props.viewed &&
        css`
            &:after {
                background-image: url('/static/img/icon-subject-inprogress.svg');
            }
            &.answered {
                &:after {
                    background-image: url('/static/img/icon-subject-completed.svg');
                }
            }
        `};
    ${(props) =>
        props.answered &&
        css`
            &:after {
                background-image: url('/static/img/icon-subject-completed.svg');
            }
        `};
    ${(props) =>
        props.incorrect &&
        css`
            &:after {
                background-image: url('/static/img/icon-subject-incorrect.svg');
            }
        `};
    ${(props) =>
        props.certificate &&
        css`
            &:after {
                background-image: url('/static/img/icon-certificate2.svg');
                background-repeat: no-repeat;
                background-size: 14px auto;
                top: 0;
            }
        `};
    ${(props) =>
        props.assessment &&
        css`
            &:after {
                background-image: url('/static/img/icon-quiz.svg');
                background-repeat: no-repeat;
                background-size: 14px auto;
                top: 0;
            }
        `};
`;

export const ThemedAnswer = styled.button
    .attrs((props) => ({
        className: [
            'ag-player-answer',
            props.correct && 'ag-player-answer-correct',
            props.incorrect && 'ag-player-answer-incorrect',
        ]
            .filter(Boolean)
            .join(' '),
    }))
    .withConfig({
        shouldForwardProp: (prop) =>
            prop !== 'correct' &&
            prop !== 'incorrect' &&
            defaultValidatorFn(prop),
    })`
    display: flex;
    align-items: center;
    justify-content: center;
    appearance: none;
    -webkit-appearance: none;
    font-family: inherit;
    border: none;
    background: ${(props) => rgba(themeColor(props), 0.1)};
    border-radius: 3px;
    transition: 0.2s ease all;
    font-size: 0.9em;
    color: ${(props) => themeColor(props)};
    font-weight: 700;
    min-height: 64px;
    text-align: center;
    min-width: 100%;
    gap: 8px;
    padding: 8px;
    cursor: pointer;
    &:hover:not(:disabled) {
        background: ${(props) => themeColor(props)};
        color: white;
    }
    &:active:not(:disabled) {
        background: ${(props) => rgba(themeColor(props), 0.4)};
        color: white;
    }
    &:focus {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${(props) => themeColor(props)};
        outline-offset: 2px;
    }
    &:disabled {
        cursor: default;
    }

    ${(props) =>
        props.correct &&
        css`
            background: ${palette.evergreen.aspargus};
            color: white;
            pointer-events: none;
        `};

    ${(props) =>
        props.incorrect &&
        css`
            background: ${palette.vibrant.raspberry};
            color: white;
            pointer-events: none;
        `};
`;
